/**
 * unitConversions.js
 *
 * Single source of truth for EVERY unit-conversion factor in the platform.
 * No other file should define a numeric conversion factor — import from here,
 * and if a unit is missing, add it here (see UNIT_OPTIONS / the section layout).
 *
 * All base constants are exact by international definition, eliminating the
 * per-cell rounding drift that accumulates when imprecise factors (e.g.
 * 907.185, 31.1035) are applied across millions of blocks.
 *
 * Internal storage units (what values are held in before display):
 *   Ore mass      → kilograms            (kg)
 *   Metal mass    → grams                (g)
 *   Grade         → g/kg
 *   Concentration → g/m³
 *   Length        → metres               (m)
 *   Area          → square metres        (m²)
 *   Volume        → cubic metres         (m³)
 *   Density       → kg/m³
 *   Pressure      → kilopascals           (kPa)
 *   Temperature   → degrees Celsius       (°C)
 *   App./leach rate, K_sat, wind speed   → metres per second (m/s)
 *   Flow rate     → cubic metres / second (m³/s)
 *   Precipitation → millimetres           (mm)
 *
 * Table naming:
 *   `X_FROM_Y[unit]`  → multiply an internal value (in Y) to reach `unit`.
 *   `TO_INTERNAL_Y`   → multiply an imported value to reach internal unit Y.
 *   `*_PER_*` tables  → inverse (internal-per-display) lookups for the chart
 *                       pipeline; derived from the forward tables so they
 *                       cannot drift out of sync.
 */

// ─── Exact physical constants — mass ──────────────────────────────────────────

/** kg per international pound  (exact by definition) */
export const LB_KG        = 0.45359237;
/** kg per US short ton  = 2 000 × LB_KG  (exact) */
export const SHORT_TON_KG = 907.18474;
/** kg per imperial long ton  = 2 240 × LB_KG  (exact) */
export const LONG_TON_KG  = 1016.0469088;
/** g per troy ounce  = 480 grains × 0.06479891 g/grain  (exact) */
export const TROY_OZ_G    = 31.1034768;
/** g per avoirdupois ounce  = LB_KG × 1000 / 16  (exact) */
export const AVD_OZ_G     = 28.349523125;

// ─── Exact physical constants — length / volume / area / pressure / speed ────

/** metres per inch  (exact by definition) */
export const IN_M     = 0.0254;
/** metres per foot  = 12 in  (exact) */
export const FT_M     = 0.3048;
/** metres per yard  = 3 ft  (exact) */
export const YD_M     = 0.9144;
/** metres per mile  = 5 280 ft  (exact) */
export const MI_M     = 1609.344;
/** litres per US gallon  (exact by definition) */
export const GAL_L    = 3.785411784;
/** m³ per cubic foot  = FT_M³  (exact) */
export const FT3_M3   = FT_M * FT_M * FT_M;       // 0.028316846592
/** m³ per cubic inch  = IN_M³  (exact) */
export const IN3_M3   = IN_M * IN_M * IN_M;       // 1.6387064e-5
/** m³ per cubic yard  = YD_M³  (exact) */
export const YD3_M3   = YD_M * YD_M * YD_M;       // 0.764554857984
/** m² per acre  = 4 840 yd²  (exact) */
export const ACRE_M2  = 4046.8564224;
/** m³ per acre-foot  = ACRE_M2 × FT_M  (exact) */
export const ACRE_FT_M3 = ACRE_M2 * FT_M;         // 1233.48183754752
/** kPa per psi  = 6 894.757293168 Pa  (exact from lbf / in² definitions) */
export const PSI_KPA  = 6.894757293168;
/** kPa per standard atmosphere  (exact by definition) */
export const ATM_KPA  = 101.325;
/** kPa per conventional millimetre of mercury  (1 mmHg = 133.322387415 Pa) */
export const MMHG_KPA = 0.133322387415;
/** kPa per inch of mercury  = 25.4 mmHg */
export const INHG_KPA = 25.4 * MMHG_KPA;          // 3.386388…
/** m/s per mile-per-hour  = 1 609.344 / 3 600  (exact) */
export const MPH_MS   = 0.44704;
/** m/s per knot  = 1 852 / 3 600  (exact) */
export const KNOT_MS  = 1852 / 3600;              // 0.514444…
/** J per International Table BTU  (exact by definition) */
export const BTU_J    = 1055.05585262;

// ─── Tonne / ounce naming convention (applies to EVERY table below) ──────────
//   "t", "tonne", "tonnes"  → metric tonne (1 000 kg). kt / Mt are its multiples.
//   "ton", "short ton"      → US short ton (2 000 lb = 907.18474 kg).
//   "long ton"              → imperial long ton (2 240 lb).
//   Ore / material mass:  "oz" = avoirdupois ounce; "troy oz" = troy.
//   Metal mass and grade: "oz" = TROY ounce (precious-metal convention);
//                         "avdp oz" is the explicit avoirdupois ounce.
// Operational-data columns saved before 2026-09-29 used the old meanings
// ("t" = short ton, metal "oz" = avoirdupois) — see LEGACY_STORED_UNITS.

// ─── Ore-mass conversion table (internal: kg) ─────────────────────────────────

/** Multiply a value stored in kg by MASS_FROM_KG[unit] to reach the display unit. */
export const MASS_FROM_KG = Object.freeze({
  mg:          1e6,
  g:           1000,
  kg:          1,
  lb:          1 / LB_KG,               // 2.204622621…
  oz:          1000 / AVD_OZ_G,         // 35.27396194…  (avoirdupois — ore/material mass)
  'troy oz':   1000 / TROY_OZ_G,        // 32.15074656…
  t:           0.001,                   // metric tonne (exact)
  tonne:       0.001,
  tonnes:      0.001,
  kt:          1e-6,                    // kilotonne = 1 000 metric tonnes (exact SI multiple of `tonne`)
  Mt:          1e-9,                    // megatonne = 1 000 000 metric tonnes (exact SI multiple of `tonne`)
  ton:         1 / SHORT_TON_KG,        // 0.001102311…  (US short ton)
  'short ton': 1 / SHORT_TON_KG,
  'long ton':  1 / LONG_TON_KG,         // 0.000984206…
});

// ─── Metal-mass conversion tables (internal: g) ──────────────────────────────

/** Multiply a value stored in grams by METAL_FROM_G[unit] to reach the display unit. */
export const METAL_FROM_G = Object.freeze({
  mg:          1000,
  g:           1,
  kg:          0.001,
  oz:          1 / TROY_OZ_G,                  // 0.032150746…  (troy — precious-metal convention)
  'troy oz':   1 / TROY_OZ_G,
  'avdp oz':   1 / AVD_OZ_G,                   // 0.035273961…  (avoirdupois, explicit)
  lb:          1 / (LB_KG * 1000),             // 0.002204622…
  t:           1e-6,                           // metric tonne
  tonne:       1e-6,
  tonnes:      1e-6,
  ton:         1 / (SHORT_TON_KG * 1000),       // 1.102311e-6  (US short ton)
  'short ton': 1 / (SHORT_TON_KG * 1000),
  'long ton':  1 / (LONG_TON_KG * 1000),        // 9.842065e-7
});

/**
 * Grams in one unit of metal mass — inverse of METAL_FROM_G.
 * Used to convert price-per-gram → price-per-display-unit, or anywhere a result
 * in grams must be divided by a unit size. Metal units only — ore mass goes
 * through MASS_FROM_KG (its "oz" is avoirdupois).
 */
export const GRAMS_PER_METAL_UNIT = Object.freeze({
  mg:          0.001,
  g:           1,
  kg:          1000,
  oz:          TROY_OZ_G,                 // 31.1034768  (troy)
  'troy oz':   TROY_OZ_G,
  'avdp oz':   AVD_OZ_G,                  // 28.349523125
  lb:          LB_KG * 1000,              // 453.59237
  t:           1e6,                       // metric tonne
  tonne:       1e6,
  tonnes:      1e6,
  ton:         SHORT_TON_KG * 1000,       // 907 184.74  (US short ton)
  'short ton': SHORT_TON_KG * 1000,
  'long ton':  LONG_TON_KG * 1000,        // 1 016 046.9088
});

// ─── Grade conversion table (internal: g/kg) ──────────────────────────────────
// Named grade units. Any other "metalUnit/oreUnit" pair (e.g. "troy oz/ton",
// "mg/tonnes") is resolved by gradeFactorFromCombined(), which agrees with
// every entry here.

/** Multiply a value stored in g/kg by GRADE_FROM_G_PER_KG[unit] to reach the display unit. */
export const GRADE_FROM_G_PER_KG = Object.freeze({
  'g/kg':         1,
  'mg/kg':        1000,
  'ppm':          1000,                       // g per metric tonne
  '%':            0.1,                         // mass percent
  'g/t':          1000,                        // g per metric tonne
  'g/tonne':      1000,
  'oz/t':         1000 / TROY_OZ_G,            // troy oz per metric tonne (32.15…)
  'oz/ton':       SHORT_TON_KG / TROY_OZ_G,    // troy oz per short ton    (29.16…)
  'oz/short ton': SHORT_TON_KG / TROY_OZ_G,
  'kg/t':         1,                           // kg per metric tonne ≡ g/kg (exact)
  'kg/tonne':     1,
  'lb/ton':       2,                           // lb per short ton — 1 short ton = 2000 lb, so 1 g/kg = 2 lb/ton (exact). Reagent doses (e.g. NaCN).
  'lb/t':         1 / LB_KG,                   // lb per metric tonne (2.2046…)
  'lb/tonne':     1 / LB_KG,
});

// ─── Volume conversion table (internal: m³) ──────────────────────────────────

/** Multiply a value stored in m³ by VOLUME_FROM_M3[unit] to reach the display unit. */
export const VOLUME_FROM_M3 = Object.freeze({
  'm³':       1,
  kL:         1,                  // kilolitre = 1 m³
  ML:         0.001,              // megalitre = 1 000 m³
  L:          1000,
  mL:         1e6,
  'cm³':      1e6,
  gal:        1000 / GAL_L,       // 264.172052…  (US gallon)
  'ft³':      1 / FT3_M3,         // 35.314666…
  'in³':      1 / IN3_M3,         // 61 023.744…
  'yd³':      1 / YD3_M3,         // 1.307950…
  'acre-ft':  1 / ACRE_FT_M3,     // 0.000810713…
  kgal:       1 / GAL_L,          // 0.264172052…  thousand US gallons (US ops convention)
});

// ─── Length conversion table (internal: m) ───────────────────────────────────

/** Multiply a value stored in m by LENGTH_FROM_M[unit] to reach the display unit. */
export const LENGTH_FROM_M = Object.freeze({
  km:  0.001,
  m:   1,
  cm:  100,
  mm:  1000,
  µm:  1e6,
  mi:  1 / MI_M,          // 0.000621371…
  yd:  1 / YD_M,          // 1.093613…
  ft:  1 / FT_M,          // 3.280839…
  in:  1 / IN_M,          // 39.370078…
});

// ─── Area conversion table (internal: m²) ────────────────────────────────────

/** Multiply a value stored in m² by AREA_FROM_M2[unit] to reach the display unit. */
export const AREA_FROM_M2 = Object.freeze({
  'm²':   1,
  'km²':  1e-6,
  'cm²':  1e4,
  'mm²':  1e6,
  ha:        1e-4,
  hectares:  1e-4,              // preference-string alias for ha
  acres:  1 / ACRE_M2,          // 0.000247105…
  'mi²':  1 / (MI_M * MI_M),    // 3.861022e-7
  'ft²':  1 / (FT_M * FT_M),    // 10.763910…
  'in²':  1 / (IN_M * IN_M),    // 1 550.0031…
  'yd²':  1 / (YD_M * YD_M),    // 1.195990…
});

// ─── Density conversion table (internal: kg/m³) ──────────────────────────────
// Composite display units whose token form ("g/cm³", "t/m³") does not decompose
// cleanly through the mass/volume tables — "t" here is a metric tonne.

/** Multiply a value stored in kg/m³ by DENSITY_FROM_KG_PER_M3[unit] to reach the display unit. */
export const DENSITY_FROM_KG_PER_M3 = Object.freeze({
  'kg/m³':  1,
  't/m³':   1e-3,                       // metric tonne per m³
  'g/cm³':  1e-3,
  'g/mL':   1e-3,
  'kg/L':   1e-3,
  'g/L':    1,
  'mg/L':   1e3,
  'lb/ft³': FT3_M3 / LB_KG,             // 0.0624279606…
  'lb/gal': (GAL_L / 1000) / LB_KG,     // 0.00834540…  (US gallon)
});

// ─── Pressure conversion table (internal: kPa) ───────────────────────────────

/** Multiply a value stored in kPa by PRESSURE_FROM_KPA[unit] to reach the display unit. */
export const PRESSURE_FROM_KPA = Object.freeze({
  kPa:   1,
  MPa:   0.001,
  Pa:    1000,
  hPa:   10,
  mbar:  10,
  bar:   0.01,
  atm:   1 / ATM_KPA,      // 0.009869…
  psi:   1 / PSI_KPA,      // 0.145037…
  mmHg:  1 / MMHG_KPA,     // 7.500617…
  torr:  1 / MMHG_KPA,     // 1 torr ≡ 1 mmHg (conventional)
  inHg:  1 / INHG_KPA,     // 0.295300…
});

// ─── Temperature: see tempFromC() — affine, not a multiply table ─────────────

// ─── Precipitation conversion table (internal: mm) ───────────────────────────

/** Multiply a value stored in mm by PRECIP_FROM_MM[unit] to reach the display unit. */
export const PRECIP_FROM_MM = Object.freeze({
  mm:  1,
  cm:  0.1,
  m:   0.001,
  in:  1 / 25.4,           // 0.039370…
  ft:  1 / 304.8,          // 0.003280…
});

// ─── Wind-speed conversion table (internal: m/s) ─────────────────────────────

/** Multiply a value stored in m/s by WIND_FROM_M_PER_S[unit] to reach the display unit. */
export const WIND_FROM_M_PER_S = Object.freeze({
  'm/s':   1,
  'km/h':  3.6,
  'ft/s':  1 / FT_M,      // 3.280839…
  mph:     1 / MPH_MS,    // 2.236936…
  knots:   1 / KNOT_MS,   // 1.943844…
});

// ─── Solar radiation conversion table (internal: MJ/m² per day) ──────────────
// Daily insolation totals (climate parquet `shortwave_radiation_sum_mj`).
// "W/m²" is the day-mean irradiance equivalent of the daily total.

/** Multiply a value stored in MJ/m² (daily total) by SOLAR_FROM_MJ_M2[unit] to reach the display unit. */
export const SOLAR_FROM_MJ_M2 = Object.freeze({
  'MJ/m²':   1,
  'kWh/m²':  1 / 3.6,                    // 1 kWh = 3.6 MJ (exact)
  'W/m²':    1e6 / 86400,                // day-mean irradiance: 11.574074…
  'BTU/ft²': (1e6 / BTU_J) * (FT_M * FT_M),  // 88.055092…
});

// ─── Application / leach-rate conversion table (internal: m/s) ────────────────
// 1 m/s of percolation = 1 m³ of solution per m² of footprint per second
// (a depth-per-time, equivalent to a volume flux per unit area).

/** Multiply a value stored in m/s by APPLICATION_RATE_FROM_M_PER_S[unit] to reach the display unit. */
export const APPLICATION_RATE_FROM_M_PER_S = Object.freeze({
  'm/s':       1,
  'm/h':       3600,
  'm/d':       86400,
  'mm/h':      3.6e6,
  'mm/d':      8.64e7,
  'cm/h':      3.6e5,
  'in/h':      3600 / IN_M,                 // 141 732.28…
  'L/(m²·s)':  1000,
  'L/(m²·h)':  3.6e6,                        // 1 mm/h over 1 m² = 1 L/(m²·h)
  'L/(m²·d)':  8.64e7,
  'gpm/ft²':   (1000 / GAL_L) * 60 * (FT_M * FT_M), // 1 472.52…  gal/min per ft²
});

// ─── Hydraulic conductivity / K_sat conversion table (internal: m/s) ─────────
// K_sat is a Darcy velocity, dimensionally identical to application rate, so its
// flux-per-area forms (L/h/m², m³/h/m²) coincide with application rate.

/** Multiply a value stored in m/s by KSAT_FROM_M_PER_S[unit] to reach the display unit. */
export const KSAT_FROM_M_PER_S = Object.freeze({
  'm/s':       1,
  'cm/s':      100,
  'mm/s':      1000,
  'm/h':       3600,
  'm/d':       86400,
  'cm/h':      3.6e5,
  'cm/d':      8.64e6,
  'mm/h':      3.6e6,
  'mm/d':      8.64e7,
  'ft/d':      86400 / FT_M,    // 283 464.57…  (hydrogeology convention)
  'in/h':      3600 / IN_M,     // 141 732.28…
  'L/h/m²':    3.6e6,
  'L/min/m²':  6e4,
  'm³/h/m²':   3600,
});

// ─── Flow-rate conversion table (internal: m³/s) ─────────────────────────────

/** Multiply a value stored in m³/s by FLOW_FROM_M3_PER_S[unit] to reach the display unit. */
export const FLOW_FROM_M3_PER_S = Object.freeze({
  'm³/s':    1,
  'm³/min':  60,
  'm³/h':    3600,
  'm³/day':  86400,
  'L/s':     1000,
  'L/min':   6e4,
  'L/h':     3.6e6,
  'L/day':   8.64e7,
  'ML/day':  86.4,                          // megalitres per day (water balance)
  'ft³/s':   1 / FT3_M3,                     // 35.314666…  (cusecs)
  'ft³/min': 60 / FT3_M3,                    // 2 118.88…   (cfm)
  'ft³/day': 86400 / FT3_M3,
  gpm:       (1000 / GAL_L) * 60,            // 15 850.32…  US gallons per minute
  'gal/min': (1000 / GAL_L) * 60,            // verbose alias for gpm
  gph:       (1000 / GAL_L) * 3600,
  'gal/day': (1000 / GAL_L) * 86400,
});

// ─── Duration conversion table (internal: hours) ─────────────────────────────
// Elapsed operating time (crusher run hours, plant run time). Internal unit is
// the hour — the unit operational logs are universally kept in.

/** Multiply a value stored in hours by DURATION_FROM_HOURS[unit] to reach the display unit. */
export const DURATION_FROM_HOURS = Object.freeze({
  h:     1,
  min:   60,
  s:     3600,
  days:  1 / 24,
  weeks: 1 / 168,
});

// ─── Rate-constant conversion table (internal: h⁻¹) ──────────────────────────
// First-order exchange / mass-transfer coefficients (HydroProperty
// massTransferCoeff, hr⁻¹). A rate per hour becomes a rate per <unit> by
// multiplying by the hours in that unit — the reciprocal of DURATION_FROM_HOURS.

/** Multiply a value stored in h⁻¹ by RATE_CONSTANT_FROM_PER_HOUR[unit] to reach the display unit. */
export const RATE_CONSTANT_FROM_PER_HOUR = Object.freeze({
  'h⁻¹':   1,
  's⁻¹':   1 / DURATION_FROM_HOURS.s,     // 1/3600
  'min⁻¹': 1 / DURATION_FROM_HOURS.min,   // 1/60
  'd⁻¹':   1 / DURATION_FROM_HOURS.days,  // 24
});

// ─── Diffusivity conversion table (internal: m²/s) ───────────────────────────
// Effective pore diffusivity (HydroProperty effectiveDiffusivityM2S). Area
// factor × seconds per time unit, composed from this module's own tables.

/** Multiply a value stored in m²/s by DIFFUSIVITY_FROM_M2_PER_S[unit] to reach the display unit. */
export const DIFFUSIVITY_FROM_M2_PER_S = Object.freeze({
  'm²/s':  1,
  'cm²/s': AREA_FROM_M2['cm²'],            // 1e4
  'mm²/s': AREA_FROM_M2['mm²'],            // 1e6
  'm²/h':  3600,
  'cm²/h': AREA_FROM_M2['cm²'] * 3600,
  'm²/d':  86400,
  'cm²/d': AREA_FROM_M2['cm²'] * 86400,
  'ft²/s': AREA_FROM_M2['ft²'],            // 10.7639…
  'ft²/d': AREA_FROM_M2['ft²'] * 86400,
});

// ─── Mass-flow / throughput conversion table (internal: kg/s) ────────────────
// Crusher / stacking / plant throughput. "t" = metric tonne (see the naming
// convention above); US short tons per hour (TPOH) is "short ton/h".

/** Multiply a value stored in kg/s by MASS_FLOW_FROM_KG_PER_S[unit] to reach the display unit. */
export const MASS_FLOW_FROM_KG_PER_S = Object.freeze({
  'kg/s':            1,
  'kg/h':            3600,
  'kg/day':          86400,
  't/h':             3.6,                      // metric tonnes per hour
  't/day':           86.4,
  'tonne/h':         3.6,
  'tonne/day':       86.4,
  'short ton/h':     3600 / SHORT_TON_KG,      // US short tons per hour (TPOH)
  'short ton/day':   86400 / SHORT_TON_KG,
  'lb/h':            3600 / LB_KG,
  'long ton/h':      3600 / LONG_TON_KG,
});

// ─── Metal production rate conversion table (internal: g/day) ────────────────
// Daily metal production / plating rates (e.g. "Au oz/day" per cell). Internal
// unit is g per day — operational logs are daily-timestep, matching the solar
// table's per-day convention. "oz" here is the troy ounce (precious-metal
// production is always reported in troy oz), spelled out for clarity.

/** Multiply a value stored in g/day by METAL_RATE_FROM_G_PER_DAY[unit] to reach the display unit. */
export const METAL_RATE_FROM_G_PER_DAY = Object.freeze({
  'g/day':       1,
  'kg/day':      0.001,
  'tonne/day':   1e-6,
  'troy oz/day': 1 / TROY_OZ_G,            // 0.032150746…
  'lb/day':      1 / (LB_KG * 1000),       // 0.002204622…
});

// ─── Concentration (flat, internal: g/m³) ────────────────────────────────────
// Single-factor mass-per-volume table for solution assays and reagent
// strengths. The composite mass/volume pair form is handled by
// concentrationFromGPerM3(value, massUnit, volUnit) below; this flat table
// exists so concentration can participate in FORWARD_TABLES / UNIT_OPTIONS
// (dynamic-domain consumers like operational-data columns).

/** Multiply a value stored in g/m³ by CONCENTRATION_FROM_G_PER_M3[unit] to reach the display unit. */
export const CONCENTRATION_FROM_G_PER_M3 = Object.freeze({
  'g/m³':    1,
  'mg/L':    1,                            // 1 g/m³ ≡ 1 mg/L (exact)
  'µg/L':    1000,
  'g/L':     1e-3,
  'mg/mL':   1e-3,
  'kg/L':    1e-6,
  'g/mL':    1e-6,
  'kg/m³':   1e-3,
  'lb/kgal': GAL_L / (LB_KG * 1000),       // 0.00834540…  lb per 1000 US gal (reagent dosing)
});

// ─── Concentration (pair form): see concentrationFromGPerM3() below ──────────

// ─── Dimensionless ratio conversion table (internal: percent, 0–100) ─────────
// Pure ratio rescaling, not a physical unit conversion; 1 % = 10 000 ppm exactly.

/** Multiply a value stored as a percent (0–100) by RATIO_FROM_PERCENT[unit] to reach the display unit. */
export const RATIO_FROM_PERCENT = Object.freeze({
  '%':       1,
  fraction:  0.01,
  decimal:   0.01,
  ratio:     0.01,
  '‰':       10,            // per mille
  ppm:       1e4,
  ppb:       1e7,
  ppt:       1e10,
});

// ─── Import-pipeline tables (display → internal) ─────────────────────────────

/** Multiply an imported mass value by TO_INTERNAL_MASS[unit] to reach kg. */
export const TO_INTERNAL_MASS = Object.freeze({
  g:           0.001,
  kg:          1,
  lb:          LB_KG,
  t:           1000,               // metric tonnes → kg
  tonne:       1000,
  tonnes:      1000,
  kt:          1e6,                // kilotonnes → kg
  Mt:          1e9,                // megatonnes → kg
  ton:         SHORT_TON_KG,       // US short tons → kg
  'short ton': SHORT_TON_KG,
  'long ton':  LONG_TON_KG,
});

/** Multiply an imported grade value by TO_INTERNAL_GRADE[unit] to reach g/kg. */
export const TO_INTERNAL_GRADE = Object.freeze({
  'g/kg':   1,
  'g/t':    0.001,
  'ppm':    0.001,
  'oz/ton': TROY_OZ_G / SHORT_TON_KG,   // exact round-trip with GRADE_FROM_G_PER_KG
  'oz/t':   TROY_OZ_G / 1000,
});

/** kg in one unit of ore mass — denominator lookup for composite grade units. */
export const ORE_MASS_TO_KG = Object.freeze({
  kg: 1,
  g: 0.001,
  lb: LB_KG,
  t: 1000,                         // metric tonne
  tonne: 1000,
  tonnes: 1000,
  ton: SHORT_TON_KG,               // US short ton
  'short ton': SHORT_TON_KG,
  'long ton': LONG_TON_KG,
  oz: AVD_OZ_G / 1000,             // ore mass: avoirdupois
  'troy oz': TROY_OZ_G / 1000,
});

// ─── Inverse tables: internal units per one display unit ─────────────────────
// The chart pipeline converts an arbitrary parquet base unit to the user's
// preferred unit as value × PER[base] / PER[target], so it needs the
// "internal-per-display" direction. These are DERIVED from the forward tables
// (reciprocal) so they can never drift; alias keys (lowercase / ascii) match the
// raw unit strings emitted by the simulation parquet schema (e.g. "m3", "ft2").

/** Build an internal-per-display table by reciprocating a forward table, plus aliases. */
function reciprocalTable(forward, aliases = {}) {
  const out = {};
  for (const k in forward) out[k] = 1 / forward[k];
  return Object.freeze(Object.assign(out, aliases));
}

/** m³ in one unit of volume. */
export const M3_PER_VOLUME_UNIT = reciprocalTable(VOLUME_FROM_M3, {
  m3: 1,
  l: 1e-3, litre: 1e-3, liter: 1e-3,
  cm3: 1e-6, ft3: FT3_M3, in3: IN3_M3, yd3: YD3_M3,
});

/** m² in one unit of area. */
export const M2_PER_AREA_UNIT = reciprocalTable(AREA_FROM_M2, {
  m2: 1, km2: 1e6, cm2: 1e-4, mm2: 1e-6,
  ft2: FT_M * FT_M, in2: IN_M * IN_M, yd2: YD_M * YD_M, mi2: MI_M * MI_M,
  ac: ACRE_M2,
});

/** m in one unit of length. */
export const M_PER_LENGTH_UNIT = reciprocalTable(LENGTH_FROM_M, {
  um: 1e-6,
});

/** kPa in one unit of pressure. */
export const KPA_PER_PRESSURE_UNIT = reciprocalTable(PRESSURE_FROM_KPA);

/** mm in one unit of precipitation. */
export const MM_PER_PRECIP_UNIT = reciprocalTable(PRECIP_FROM_MM);

/** m/s equivalent of one unit of wind speed. */
export const MS_PER_WIND_UNIT = reciprocalTable(WIND_FROM_M_PER_S);

/** MJ/m² (daily total) in one unit of solar radiation. */
export const MJ_M2_PER_SOLAR_UNIT = reciprocalTable(SOLAR_FROM_MJ_M2);

/**
 * Fractional days per one unit of time.
 * Used to convert denominators in rate units (e.g. m³/day → m³/min).
 */
export const DAYS_PER_TIME_UNIT = Object.freeze({
  ms: 1 / 86400000,
  s: 1 / 86400, sec: 1 / 86400, second: 1 / 86400, seconds: 1 / 86400,
  min: 1 / 1440, minute: 1 / 1440, minutes: 1 / 1440,
  h: 1 / 24, hr: 1 / 24, hour: 1 / 24, hours: 1 / 24,
  d: 1, day: 1, days: 1,
  week: 7, weeks: 7,
  month: 30, months: 30,   // nominal 30-day month (matches the preference picker)
});

// ─── Scalar conversion helpers ────────────────────────────────────────────────
// Each throws on an unknown unit so a bad preference string surfaces loudly.

/** Convert kilograms to any supported ore-mass display unit. */
export function massFromKg(kg, unit) {
  const f = MASS_FROM_KG[unit];
  if (f == null) throw new Error(`Unknown mass unit: "${unit}"`);
  return kg * f;
}

/** Convert grams to any supported metal-mass display unit. */
export function metalFromG(grams, unit) {
  const f = METAL_FROM_G[unit];
  if (f == null) throw new Error(`Unknown metal unit: "${unit}"`);
  return grams * f;
}

/** Convert g/kg to any supported grade display unit. */
export function gradeFromGPerKg(gPerKg, unit) {
  const f = gradeFactor(unit);
  if (f == null) throw new Error(`Unknown grade unit: "${unit}"`);
  return gPerKg * f;
}

/** Convert m³ to any supported volume display unit. */
export function volumeFromM3(m3, unit) {
  const f = VOLUME_FROM_M3[unit];
  if (f == null) throw new Error(`Unknown volume unit: "${unit}"`);
  return m3 * f;
}

/** Convert metres to any supported length display unit. */
export function lengthFromM(m, unit) {
  const f = LENGTH_FROM_M[unit];
  if (f == null) throw new Error(`Unknown length unit: "${unit}"`);
  return m * f;
}

/** Convert m² to any supported area display unit. */
export function areaFromM2(m2, unit) {
  const f = AREA_FROM_M2[unit];
  if (f == null) throw new Error(`Unknown area unit: "${unit}"`);
  return m2 * f;
}

/**
 * Convert a density stored in kg/m³ to a massUnit/volUnit pair.
 * Density is a ratio, so both the numerator (mass) and denominator (volume)
 * factors apply — the volume factor divides because it sits in the denominator.
 */
export function densityFromKgPerM3(value, massUnit, volUnit) {
  const mf = MASS_FROM_KG[massUnit];
  const vf = VOLUME_FROM_M3[volUnit];
  if (mf == null) throw new Error(`Unknown mass unit: "${massUnit}"`);
  if (vf == null) throw new Error(`Unknown volume unit: "${volUnit}"`);
  return value * mf / vf;
}

/** Convert a density in kg/m³ to a composite display unit such as "g/cm³" or "lb/ft³". */
export function densityFromKgPerM3Unit(value, unit) {
  const f = DENSITY_FROM_KG_PER_M3[unit];
  if (f == null) throw new Error(`Unknown density unit: "${unit}"`);
  return value * f;
}

/** Convert kPa to any supported pressure display unit. */
export function pressureFromKpa(kpa, unit) {
  const f = PRESSURE_FROM_KPA[unit];
  if (f == null) throw new Error(`Unknown pressure unit: "${unit}"`);
  return kpa * f;
}

/** Convert mm to any supported precipitation display unit. */
export function precipFromMm(mm, unit) {
  const f = PRECIP_FROM_MM[unit];
  if (f == null) throw new Error(`Unknown precipitation unit: "${unit}"`);
  return mm * f;
}

/** Convert m/s to any supported wind-speed display unit. */
export function windFromMs(ms, unit) {
  const f = WIND_FROM_M_PER_S[unit];
  if (f == null) throw new Error(`Unknown wind-speed unit: "${unit}"`);
  return ms * f;
}

/** Convert a daily solar-radiation total in MJ/m² to any supported display unit. */
export function solarFromMjM2(mj, unit) {
  const f = SOLAR_FROM_MJ_M2[unit];
  if (f == null) throw new Error(`Unknown solar-radiation unit: "${unit}"`);
  return mj * f;
}

/** Convert m/s percolation to any supported application-rate display unit. */
export function applicationRateFromMPerS(ms, unit) {
  const f = APPLICATION_RATE_FROM_M_PER_S[unit];
  if (f == null) throw new Error(`Unknown application-rate unit: "${unit}"`);
  return ms * f;
}

/** Default application-rate unit when the user has no (usable) preference. */
export const DEFAULT_APPLICATION_RATE_UNIT = 'L/(m²·h)';

/**
 * Map a user's application-rate preference (UnitPreferencesContext shape:
 * leaching.applicationRate.combined = "vol/time/area", e.g. "L/h/m²") to the
 * canonical APPLICATION_RATE_FROM_M_PER_S key (e.g. "L/(m²·h)"). Falls back to
 * DEFAULT_APPLICATION_RATE_UNIT when the preference is absent or unsupported.
 */
export function applicationRateUnitFromPreference(unitPreferences) {
  const combined = unitPreferences?.leaching?.applicationRate?.combined;
  if (combined) {
    const [vol, time, area] = combined.split('/');
    const candidate = `${vol}/(${area}·${time})`;
    if (APPLICATION_RATE_FROM_M_PER_S[candidate] != null) return candidate;
  }
  return DEFAULT_APPLICATION_RATE_UNIT;
}

/**
 * Resolve an application-rate preference string ("volume/time/area", e.g.
 * "L/h/m²" or "gal/min/ft²") to { factor, unit, resolved }, where
 * internal m/s × factor = display value.
 *
 * The settings picker offers far more volume × time × area combinations than
 * APPLICATION_RATE_FROM_M_PER_S has canonical keys for, so this resolves in
 * three steps:
 *   1. a combination that has a canonical key uses that key's exact factor
 *      (so it agrees with applicationRateUnitFromPreference by construction);
 *   2. any other combination of known volume/time/area units is composed from
 *      this module's own tables;
 *   3. an absent or unrecognised combination falls back to
 *      DEFAULT_APPLICATION_RATE_UNIT with `resolved: false`, letting the caller
 *      decide whether to keep displaying the user's raw string.
 *
 * Use this over applicationRateUnitFromPreference when you need a numeric
 * factor; use that one when you need a key into a unit table.
 */
export function applicationRateFactorFromCombined(combined) {
  if (combined) {
    const parts = combined.split('/');
    if (parts.length === 3) {
      const [vol, time, area] = parts;

      const canonical = `${vol}/(${area}·${time})`;
      if (APPLICATION_RATE_FROM_M_PER_S[canonical] != null) {
        return {
          factor: APPLICATION_RATE_FROM_M_PER_S[canonical],
          unit: canonical,
          resolved: true,
        };
      }

      const volPerM3  = VOLUME_FROM_M3[vol];      // display volume units per m³
      const days      = DAYS_PER_TIME_UNIT[time]; // days per time unit
      const areaPerM2 = AREA_FROM_M2[area];       // display area units per m²
      if (volPerM3 != null && days != null && areaPerM2 != null) {
        // m/s is m³/(m²·s), so rebase to L/(m²·d) and then swap in the display
        // volume, time and area units.
        const factor = APPLICATION_RATE_FROM_M_PER_S['L/(m²·d)']
          * (volPerM3 / VOLUME_FROM_M3.L) * days / areaPerM2;
        return { factor, unit: combined, resolved: true };
      }
    }
  }
  return {
    factor: APPLICATION_RATE_FROM_M_PER_S[DEFAULT_APPLICATION_RATE_UNIT],
    unit: DEFAULT_APPLICATION_RATE_UNIT,
    resolved: false,
  };
}

/** Convert a hydraulic conductivity (K_sat) in m/s to any supported display unit. */
export function ksatFromMPerS(ms, unit) {
  const f = KSAT_FROM_M_PER_S[unit];
  if (f == null) throw new Error(`Unknown K_sat unit: "${unit}"`);
  return ms * f;
}

/** Convert m³/s to any supported flow-rate display unit. */
export function flowFromM3PerS(m3s, unit) {
  const f = FLOW_FROM_M3_PER_S[unit];
  if (f == null) throw new Error(`Unknown flow-rate unit: "${unit}"`);
  return m3s * f;
}

/** Convert hours to any supported duration display unit. */
export function durationFromHours(hours, unit) {
  const f = DURATION_FROM_HOURS[unit];
  if (f == null) throw new Error(`Unknown duration unit: "${unit}"`);
  return hours * f;
}

/** Convert a first-order rate constant in h⁻¹ to any supported display unit. */
export function rateConstantFromPerHour(perHour, unit) {
  const f = RATE_CONSTANT_FROM_PER_HOUR[unit];
  if (f == null) throw new Error(`Unknown rate-constant unit: "${unit}"`);
  return perHour * f;
}

/** Convert m²/s to any supported diffusivity display unit. */
export function diffusivityFromM2PerS(m2s, unit) {
  const f = DIFFUSIVITY_FROM_M2_PER_S[unit];
  if (f == null) throw new Error(`Unknown diffusivity unit: "${unit}"`);
  return m2s * f;
}

/** Convert kg/s to any supported mass-flow / throughput display unit. */
export function massFlowFromKgPerS(kgPerS, unit) {
  const f = MASS_FLOW_FROM_KG_PER_S[unit];
  if (f == null) throw new Error(`Unknown mass-flow unit: "${unit}"`);
  return kgPerS * f;
}

/** Convert g/day to any supported metal-production-rate display unit. */
export function metalRateFromGPerDay(gPerDay, unit) {
  const f = METAL_RATE_FROM_G_PER_DAY[unit];
  if (f == null) throw new Error(`Unknown metal-rate unit: "${unit}"`);
  return gPerDay * f;
}

/** Convert g/m³ to a composite concentration unit key ("mg/L", "lb/kgal", …). */
export function concentrationFromGPerM3Unit(value, unit) {
  const f = CONCENTRATION_FROM_G_PER_M3[unit];
  if (f == null) throw new Error(`Unknown concentration unit: "${unit}"`);
  return value * f;
}

/**
 * Convert a concentration stored in g/m³ to a massUnit/volUnit pair (e.g. "g/L",
 * "mg/L"). Like density, the volume factor divides (denominator).
 */
export function concentrationFromGPerM3(value, massUnit, volUnit) {
  const mf = METAL_FROM_G[massUnit];
  const vf = VOLUME_FROM_M3[volUnit];
  if (mf == null) throw new Error(`Unknown metal unit: "${massUnit}"`);
  if (vf == null) throw new Error(`Unknown volume unit: "${volUnit}"`);
  return value * mf / vf;
}

/** Convert a percent (0–100) to any supported dimensionless ratio unit. */
export function ratioFromPercent(percent, unit) {
  const f = RATIO_FROM_PERCENT[unit];
  if (f == null) throw new Error(`Unknown ratio unit: "${unit}"`);
  return percent * f;
}

/**
 * Convert a temperature in °C to the requested unit.
 * Temperature is an affine (offset) conversion, not a simple scale factor,
 * so it cannot live in a multiply-only lookup table.
 */
export function tempFromC(celsius, unit) {
  if (celsius == null) return celsius;
  switch (unit) {
    case '°F':
    case 'F':
      return celsius * 9 / 5 + 32;
    case 'K':
      return celsius + 273.15;
    case '°R':
    case 'R':                                 // Rankine
      return (celsius + 273.15) * 9 / 5;
    case '°C':
    case 'C':
    default:
      return celsius;
  }
}

/** Inverse of tempFromC: convert a temperature in `unit` back to °C. */
export function tempToC(value, unit) {
  if (value == null) return value;
  switch (unit) {
    case '°F':
    case 'F':
      return (value - 32) * 5 / 9;
    case 'K':
      return value - 273.15;
    case '°R':
    case 'R':
      return value * 5 / 9 - 273.15;
    case '°C':
    case 'C':
    default:
      return value;
  }
}

/**
 * Factor that converts a grade stored in g/kg into `unit`, or null when the
 * unit cannot be resolved. Named units come from GRADE_FROM_G_PER_KG; any other
 * "metalUnit/oreUnit" pair (the Settings grade picker builds these freely, e.g.
 * "troy oz/ton", "mg/tonnes") is composed from GRAMS_PER_METAL_UNIT (metal
 * "oz" = troy) and ORE_MASS_TO_KG ("t" = metric, "ton" = short ton).
 */
export function gradeFactor(unit) {
  if (!unit) return null;
  const named = GRADE_FROM_G_PER_KG[unit];
  if (named != null) return named;
  const parts = String(unit).split('/');
  if (parts.length !== 2) return null;
  const gramsPerMetal = GRAMS_PER_METAL_UNIT[parts[0].trim()];
  const kgPerOre = ORE_MASS_TO_KG[parts[1].trim()];
  if (gramsPerMetal == null || kgPerOre == null) return null;
  // 1 g/kg = (1/gramsPerMetal metal units per g) × (kgPerOre kg per ore unit).
  return kgPerOre / gramsPerMetal;
}

/**
 * Resolve a grade display unit (usually the user's gradeOre.combined
 * preference) to { factor, unit, resolved }. An unresolvable unit falls back to
 * g/t and says so in `unit`, so a value is never shown under a label it was not
 * converted to.
 */
export function gradeFactorFromCombined(combined) {
  if (!combined) return { factor: 1, unit: 'g/kg', resolved: true }; // no preference → internal unit
  const f = gradeFactor(combined);
  if (f != null) return { factor: f, unit: combined, resolved: true };
  return { factor: GRADE_FROM_G_PER_KG['g/t'], unit: 'g/t', resolved: false };
}

// ─── Units saved before the 2026-09-29 tonne/ounce convention change ─────────
// Operational-data columns store the unit their values were entered in. Before
// the change, "t" meant a US short ton and a metal "oz" meant avoirdupois; this
// map re-reads those saved ids under their original meaning. New saves never
// write these bare ids (canonicalStoredUnit), so the mapping is unambiguous.
export const LEGACY_STORED_UNITS = Object.freeze({
  mass:      Object.freeze({ t: 'short ton' }),
  metalMass: Object.freeze({ t: 'short ton', oz: 'avdp oz' }),
  massFlow:  Object.freeze({ 't/h': 'short ton/h', 't/day': 'short ton/day' }),
  grade:     Object.freeze({ 'lb/t': 'lb/ton' }),
});

// The same ids under the current meaning, spelled unambiguously for saving.
const CANONICAL_STORED_UNITS = Object.freeze({
  mass:      Object.freeze({ t: 'tonne' }),
  metalMass: Object.freeze({ t: 'tonne', oz: 'troy oz' }),
  massFlow:  Object.freeze({ 't/h': 'tonne/h', 't/day': 'tonne/day' }),
  grade:     Object.freeze({ 'lb/t': 'lb/tonne' }),
});

/** Unit a previously SAVED value is actually in (legacy ids re-read). */
export function resolveStoredUnit(domain, unit) {
  return LEGACY_STORED_UNITS[domain]?.[unit] ?? unit;
}

/** Unit id to SAVE for a unit chosen now — never one of the legacy bare ids. */
export function canonicalStoredUnit(domain, unit) {
  return CANONICAL_STORED_UNITS[domain]?.[unit] ?? unit;
}

// ─── Unified registry + dispatcher ───────────────────────────────────────────
// One place to enumerate every domain's available units (for unit-picker UIs)
// and to convert an internal value without importing each table by hand.

/** Single-factor forward tables keyed by domain name. */
export const FORWARD_TABLES = Object.freeze({
  mass:            MASS_FROM_KG,
  metalMass:       METAL_FROM_G,
  grade:           GRADE_FROM_G_PER_KG,
  volume:          VOLUME_FROM_M3,
  length:          LENGTH_FROM_M,
  area:            AREA_FROM_M2,
  density:         DENSITY_FROM_KG_PER_M3,
  pressure:        PRESSURE_FROM_KPA,
  precipitation:   PRECIP_FROM_MM,
  windSpeed:       WIND_FROM_M_PER_S,
  solarRadiation:  SOLAR_FROM_MJ_M2,
  applicationRate: APPLICATION_RATE_FROM_M_PER_S,
  ksat:            KSAT_FROM_M_PER_S,
  flowRate:        FLOW_FROM_M3_PER_S,
  ratio:           RATIO_FROM_PERCENT,
  duration:        DURATION_FROM_HOURS,
  rateConstant:    RATE_CONSTANT_FROM_PER_HOUR,
  diffusivity:     DIFFUSIVITY_FROM_M2_PER_S,
  massFlow:        MASS_FLOW_FROM_KG_PER_S,
  metalRate:       METAL_RATE_FROM_G_PER_DAY,
  concentration:   CONCENTRATION_FROM_G_PER_M3,
});

/** Every supported display unit per domain — drive dropdowns from this. */
export const UNIT_OPTIONS = Object.freeze({
  mass:            Object.freeze(Object.keys(MASS_FROM_KG)),
  metalMass:       Object.freeze(Object.keys(METAL_FROM_G)),
  grade:           Object.freeze(Object.keys(GRADE_FROM_G_PER_KG)),
  volume:          Object.freeze(Object.keys(VOLUME_FROM_M3)),
  length:          Object.freeze(Object.keys(LENGTH_FROM_M)),
  area:            Object.freeze(Object.keys(AREA_FROM_M2)),
  density:         Object.freeze(Object.keys(DENSITY_FROM_KG_PER_M3)),
  pressure:        Object.freeze(Object.keys(PRESSURE_FROM_KPA)),
  temperature:     Object.freeze(['°C', '°F', 'K', '°R']),
  precipitation:   Object.freeze(Object.keys(PRECIP_FROM_MM)),
  windSpeed:       Object.freeze(Object.keys(WIND_FROM_M_PER_S)),
  solarRadiation:  Object.freeze(Object.keys(SOLAR_FROM_MJ_M2)),
  applicationRate: Object.freeze(Object.keys(APPLICATION_RATE_FROM_M_PER_S)),
  ksat:            Object.freeze(Object.keys(KSAT_FROM_M_PER_S)),
  flowRate:        Object.freeze(Object.keys(FLOW_FROM_M3_PER_S)),
  ratio:           Object.freeze(Object.keys(RATIO_FROM_PERCENT)),
  duration:        Object.freeze(Object.keys(DURATION_FROM_HOURS)),
  rateConstant:    Object.freeze(Object.keys(RATE_CONSTANT_FROM_PER_HOUR)),
  diffusivity:     Object.freeze(Object.keys(DIFFUSIVITY_FROM_M2_PER_S)),
  massFlow:        Object.freeze(Object.keys(MASS_FLOW_FROM_KG_PER_S)),
  metalRate:       Object.freeze(Object.keys(METAL_RATE_FROM_G_PER_DAY)),
  concentration:   Object.freeze(Object.keys(CONCENTRATION_FROM_G_PER_M3)),
});

/**
 * Convert an internal value to a display unit for any single-factor domain
 * listed in FORWARD_TABLES (plus "temperature", handled affinely).
 * Composite domains (density-pair, concentration) use their dedicated helpers.
 */
export function convertFromInternal(domain, value, unit) {
  if (domain === 'temperature') return tempFromC(value, unit);
  const table = FORWARD_TABLES[domain];
  if (!table) throw new Error(`Unknown conversion domain: "${domain}"`);
  const f = table[unit];
  if (f == null) throw new Error(`Unknown ${domain} unit: "${unit}"`);
  return value * f;
}

// ─── Vectorized conversion helpers ───────────────────────────────────────────
// Operate on typed arrays for per-cell operations over heap block data
// (potentially millions of values). Always return a new Float64Array.

/** Apply a single multiplicative factor across an array, returning a Float64Array. */
function scaleArray(arr, f) {
  const out = new Float64Array(arr.length);
  for (let i = 0; i < arr.length; i++) out[i] = arr[i] * f;
  return out;
}

/** Convert a typed array of kg values to the requested ore-mass display unit. */
export function massArrayFromKg(arr, unit) {
  const f = MASS_FROM_KG[unit];
  if (f == null) throw new Error(`Unknown mass unit: "${unit}"`);
  return scaleArray(arr, f);
}

/** Convert a typed array of gram values to the requested metal-mass display unit. */
export function metalArrayFromG(arr, unit) {
  const f = METAL_FROM_G[unit];
  if (f == null) throw new Error(`Unknown metal unit: "${unit}"`);
  return scaleArray(arr, f);
}

/** Convert a typed array of g/kg values to the requested grade display unit. */
export function gradeArrayFromGPerKg(arr, unit) {
  const f = gradeFactor(unit);
  if (f == null) throw new Error(`Unknown grade unit: "${unit}"`);
  return scaleArray(arr, f);
}

/** Convert a typed array of metre values to the requested length display unit. */
export function lengthArrayFromM(arr, unit) {
  const f = LENGTH_FROM_M[unit];
  if (f == null) throw new Error(`Unknown length unit: "${unit}"`);
  return scaleArray(arr, f);
}

/** Convert a typed array of m³ values to the requested volume display unit. */
export function volumeArrayFromM3(arr, unit) {
  const f = VOLUME_FROM_M3[unit];
  if (f == null) throw new Error(`Unknown volume unit: "${unit}"`);
  return scaleArray(arr, f);
}
